/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { defineMessages } from 'react-intl';

import { formatErrorResponse } from 'responseFormatterFunctions';
import { I18NError } from 'Utils';
import { fetchGet, fetchPost, fetchPut } from 'FetchAPIs';

const baseHierarchiesURL = '/api/hierarchies';

const messages = defineMessages({
  errorLoadingSystemsForSite: 'Error loading systems for site. {message}',
  errorLoadingSystemForSite: 'Error loading systems for site. {message}',
  errorLoadingAssetsForSite: 'Error loading assets for site. {message}',
  errorLoadingAssetForSite: 'Error loading asset for site. {message}',
  errorLoadingSubAssetsForAsset: 'Error loading sub assets for asset. {message}',
  errorLoadingParentAssetsForAsset: 'Error loading parent assets for asset. {message}',
  errorLoadingSites: 'Error loading sites. {message}',
  errorLoadingSite: 'Error loading site. {message}',
  errorLoadingLocationsForSite: 'Error loading locations for site. {message}',
  errorLoadingLocationForSite: 'Error loading location for site. {message}',
  errorLoadingDevicesForSiteAssets: 'Error loading devices for asset. {message}',
  errorRemovingDevicesFromSiteAssets: 'Error removing devices from asset. {message}',
});

export const getSites = async (userEmailId: string): Promise<any> =>
  fetchPost(`${baseHierarchiesURL}/sites/search`, null, { userEmailId }, null)
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorLoadingSites, {
        message: formatErrorResponse(error),
      });
    });

/**
 * Function  that loads Site API
 * @returns details for site
 */
export const getSiteByIdAPI = async (siteId: string): Promise<any> =>
  fetchGet(`${baseHierarchiesURL}/sites/${siteId}`, null, null)
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorLoadingSite, {
        message: formatErrorResponse(error),
      });
    });

/**
 * Function that loads systems for a specific site API
 * @param siteId - site id
 * @returns existing systems for the specified site
 */
export const getSystemsForSiteAPI = async (siteId: string): Promise<any> =>
  fetchGet(`${baseHierarchiesURL}/sites/${siteId}/systems`, null, null)
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorLoadingSystemsForSite, {
        message: formatErrorResponse(error),
      });
    });

/**
 * Function that loads a specific system for a specific site API
 * @param siteId - site id
 * @param systemId - system id
 * @returns existing system for the specified site and system id provided
 */
export const getSystemByIdForSite = async (siteId: string, systemId: string): Promise<any> =>
  fetchGet(`${baseHierarchiesURL}/sites/${siteId}/systems/${systemId}`, null, null)
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorLoadingSystemForSite, {
        message: formatErrorResponse(error),
      });
    });

/**
 * Function that loads location by it's id for a specific site API
 * @param siteId -  site id
 * @param locationId - location id
 * @returns existing location data for the specified site and location
 */
export const getLocationByIdForSiteAPI = async (siteId: string, locationId: string): Promise<any> =>
  fetchGet(`${baseHierarchiesURL}/sites/${siteId}/locations/${locationId}`, null, null)
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorLoadingLocationForSite, {
        message: formatErrorResponse(error),
      });
    });

/**
 * Function that loads locations for a specific site API
 * @param siteId - site id
 * @returns existing locations for the specified site
 */
export const getLocationsForSiteAPI = async (siteId: string): Promise<any> =>
  fetchGet(`${baseHierarchiesURL}/sites/${siteId}/locations`, null, null)
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorLoadingLocationsForSite, {
        message: formatErrorResponse(error),
      });
    });
/**
 * Function that loads asset for a specific site API
 * @param siteId -  site id
 * @returns existing asset data for the specified site
 */
export const getAssetsForSiteAPI = async (siteId: string): Promise<any> =>
  fetchGet(`${baseHierarchiesURL}/sites/${siteId}/assets`, null, null)
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorLoadingAssetsForSite, {
        message: formatErrorResponse(error),
      });
    });

/**
 * Function that loads asset by it's id for a specific site API
 * @param siteId -  site id
 * @param assetId - asset id
 * @returns existing asset data for the specified site and asset
 */
export const getAssetByIdForSiteAPI = async (siteId: string, assetId: string): Promise<any> =>
  fetchGet(`${baseHierarchiesURL}/sites/${siteId}/assets/${assetId}`, null, null)
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorLoadingAssetForSite, {
        message: formatErrorResponse(error),
      });
    });

/**
 * Function that loads assigned devices for a specific asset
 * @param siteId -  site id
 * @param assetId - asset id
 * @returns assigned devices for the specified site and asset
 */
export const getDevicesForAsset = async (
  siteId: string,
  assetId: string,
  page?: number,
  limit?: number
): Promise<any> =>
  fetchGet(`${baseHierarchiesURL}/sites/${siteId}/assets/${assetId}/devices`, null, {
    page: page || 0,
    limit: limit || 100,
  })
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorLoadingDevicesForSiteAssets, {
        message: formatErrorResponse(error),
      });
    });

/**
 * Function that loads subAssets for a specific asset
 * @param siteId -  site id
 * @param assetId - asset id
 * @returns child assets for the specified site and asset
 */
export const getSubAssetsForAsset = async (
  siteId: string,
  assetId: string,
  page: number,
  limit: number
): Promise<any> =>
  fetchGet(`${baseHierarchiesURL}/sites/${siteId}/assets/${assetId}/subAssets`, null, {
    page,
    limit,
  })
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorLoadingSubAssetsForAsset, {
        message: formatErrorResponse(error),
      });
    });

/**
 * Function that loads parent assets a specific asset
 * @param siteId -  site id
 * @param assetId - asset id
 * @returns parent assets for the specified site and asset
 */
export const getParentAssetsForAsset = async (siteId: string, assetId: string): Promise<any> =>
  fetchGet(`${baseHierarchiesURL}/sites/${siteId}/assets/${assetId}/parentAssets`, null, null)
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorLoadingParentAssetsForAsset, {
        message: formatErrorResponse(error),
      });
    });

/**
 * Function that removes devices from an asset API
 * @param siteId -  site id
 * @param assetId - asset id
 * @param deviceDDIds - array of deviceDDIds to be removed from the asset
 */
export const removeDevicesFromSiteAsset = async (
  siteId: string,
  assetId: string,
  deviceDDIds: string[],
  userEmailId: string
): Promise<any> =>
  fetchPut(
    `${baseHierarchiesURL}/sites/${siteId}/assets/${assetId}/relations/devices/remove`,
    deviceDDIds,
    { userEmailId },
    undefined
  ).catch(error => {
    throw new I18NError(messages.errorRemovingDevicesFromSiteAssets, {
      message: formatErrorResponse(error),
    });
  });
